import type { ProctorAllocation } from "../native-modules/types/JSTProctorBridge";
import { parsePrforceGroups } from "./parsePrforceGroups";

/**
 * Imperative (non-React) store for Proctor bucket overrides sourced from the
 * native `prforceGroups` string (debug "forced web groups", Iris `--proctor`,
 * etc.).
 *
 * The generated `…ProctorProvider` components already apply these overrides via
 * `applyProctorOverride`, but that only reaches code reading allocations through
 * React context. Imperative, non-React callers—most notably the synchronous
 * `getAccountStatusSync` path in `tareCodegenMigration`—cannot use hooks, so
 * they read the parsed override map from here instead.
 *
 * Seed it once, as early as possible, from a component that has access to the
 * native context (see `NativeContextProvider`). Because seeding happens inside
 * React while some consumers can run synchronously before the first seed, reads
 * are best-effort: callers should fall back to the raw TARE allocation until the
 * store is populated.
 */
let overrideAllocations: Record<string, ProctorAllocation> = {};

/**
 * Parse a native `prforceGroups` string and replace the current override map.
 * Passing an empty/null/undefined value clears any existing overrides.
 */
export function setProctorOverridesFromPrforceGroups(
  prforceGroups: string | null | undefined,
): void {
  overrideAllocations = parsePrforceGroups(prforceGroups ?? "");
}

/**
 * Read the current override map. Returns an empty record when nothing has been
 * forced. Safe to pass straight into `applyProctorOverride`.
 */
export function getProctorOverrides(): Record<string, ProctorAllocation> {
  return overrideAllocations;
}

/**
 * Reset the store. Primarily for tests so overrides don't leak across cases.
 */
export function clearProctorOverrides(): void {
  overrideAllocations = {};
}
